/**
 * Document state with undo/redo.
 *
 * History is a stack of whole documents rather than a log of inverse
 * operations. A flat's plan is a few tens of kilobytes, so snapshots cost
 * nothing measurable, and they cannot drift out of sync with the document the
 * way hand-written undo handlers eventually do. Every edit already returns a new
 * Level (see model/graph.ts), so pushing one is just keeping the old reference.
 *
 * All of it lives in a single state object, and every callback is created once
 * with an empty dependency list. That is deliberate and load-bearing: these
 * callbacks are dependencies of the caller's effects, so a callback that
 * changed identity on each edit would re-run the caller's document-loading
 * effect on each edit, refetch the saved document and silently throw the user's
 * work away. That bug shipped once; usePlanDocument.test.tsx pins it shut.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import type { Level, PlanDocument } from '../model/types';

/** Deep enough history to undo a tracing session, shallow enough to bound memory. */
const MAX_HISTORY = 200;

interface HistoryState {
  entries: PlanDocument[];
  index: number;
  /** Index of the revision last committed to the server. */
  savedIndex: number;
}

export interface PlanDocumentState {
  document: PlanDocument;
  level: Level;
  levelIndex: number;
  setLevelIndex: (index: number) => void;
  /** Applies an edit to the current level and pushes it onto the history. */
  editLevel: (edit: (level: Level) => Level) => void;
  /** Replaces the whole document, e.g. after loading a revision. */
  replaceDocument: (document: PlanDocument, options?: { resetHistory?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when there are edits not yet committed to a revision. */
  dirty: boolean;
  markSaved: () => void;
}

/** Appends a document, dropping the oldest entries past the cap. */
function append(state: HistoryState, next: PlanDocument): HistoryState {
  // Editing after an undo discards the redo branch, as every editor does.
  const entries = [...state.entries.slice(0, state.index + 1), next];
  let index = entries.length - 1;
  let savedIndex = state.savedIndex;

  if (entries.length > MAX_HISTORY) {
    const dropped = entries.length - MAX_HISTORY;
    entries.splice(0, dropped);
    index -= dropped;
    // Goes negative once the saved point falls off the back, which correctly
    // leaves the document dirty: it can no longer be proven unchanged.
    savedIndex -= dropped;
  }
  return { entries, index, savedIndex };
}

export function usePlanDocument(initial: PlanDocument): PlanDocumentState {
  const [state, setState] = useState<HistoryState>({
    entries: [initial],
    index: 0,
    savedIndex: 0,
  });
  const [levelIndex, setLevelIndex] = useState(0);

  // Read inside editLevel without making it a dependency, which would
  // reintroduce the unstable-identity problem this hook exists to avoid.
  const levelIndexRef = useRef(levelIndex);
  levelIndexRef.current = levelIndex;

  const document = state.entries[state.index];
  const boundedLevelIndex = Math.min(levelIndex, document.levels.length - 1);
  const level = document.levels[boundedLevelIndex];

  const editLevel = useCallback((edit: (level: Level) => Level) => {
    setState((current) => {
      const doc = current.entries[current.index];
      const target = Math.min(levelIndexRef.current, doc.levels.length - 1);
      const nextLevel = edit(doc.levels[target]);

      // A no-op edit -- clicking where nothing happens -- must not create an
      // undo step the user then has to press twice to get past.
      if (nextLevel === doc.levels[target]) return current;

      const levels = doc.levels.slice();
      levels[target] = nextLevel;
      return append(current, { ...doc, levels });
    });
  }, []);

  const replaceDocument = useCallback(
    (next: PlanDocument, options?: { resetHistory?: boolean }) => {
      setState((current) =>
        options?.resetHistory
          ? { entries: [next], index: 0, savedIndex: 0 }
          : append(current, next),
      );
    },
    [],
  );

  const undo = useCallback(() => {
    setState((current) => ({ ...current, index: Math.max(0, current.index - 1) }));
  }, []);

  const redo = useCallback(() => {
    setState((current) => ({
      ...current,
      index: Math.min(current.entries.length - 1, current.index + 1),
    }));
  }, []);

  const markSaved = useCallback(() => {
    setState((current) => ({ ...current, savedIndex: current.index }));
  }, []);

  return useMemo(
    () => ({
      document,
      level,
      levelIndex: boundedLevelIndex,
      setLevelIndex,
      editLevel,
      replaceDocument,
      undo,
      redo,
      canUndo: state.index > 0,
      canRedo: state.index < state.entries.length - 1,
      dirty: state.index !== state.savedIndex,
      markSaved,
    }),
    [
      document,
      level,
      boundedLevelIndex,
      editLevel,
      replaceDocument,
      undo,
      redo,
      markSaved,
      state.index,
      state.entries.length,
      state.savedIndex,
    ],
  );
}
